// src/pages/agent/home/components/TableMatchAgentLecs.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGridPro } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import API from "../../../../api";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Europe/Rome";
const SCAN_GROUP_WINDOW_MS = 2 * 60 * 1000;

function safeStr(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function normalizeList(resData) {
  if (Array.isArray(resData)) return resData;
  if (Array.isArray(resData?.data)) return resData.data;
  if (Array.isArray(resData?.results)) return resData.results;
  if (Array.isArray(resData?.items)) return resData.items;
  return [];
}

function mapSeverity(sev) {
  if (sev === 0 || sev === "0") return "Critical";
  if (sev === 1 || sev === "1") return "High";
  if (sev === 2 || sev === "2") return "Alert";
  if (sev === 3 || sev === "3") return "Movement";
  if (sev === 4 || sev === "4") return "Threat Intelligence";
  if (sev === 5 || sev === "5") return "5";
  return safeStr(sev) || "-";
}

function formatDate(value) {
  if (!value) return "-";
  const d = dayjs(value);
  if (!d.isValid()) return "-";
  return d.tz(TZ).format("DD/MM/YYYY HH:mm:ss");
}

function formatIpPort(ip, port) {
  const ipStr = safeStr(ip).trim();
  const portStr = safeStr(port).trim();

  if (!ipStr && !portStr) return "-";
  if (!ipStr) return `:${portStr}`;
  if (!portStr) return ipStr;
  return `${ipStr}:${portStr}`;
}

function toMillis(value) {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function uniqueArray(values) {
  const flat = Array.isArray(values) ? values.flat(Infinity) : [values];
  return [...new Set(flat.map((v) => safeStr(v).trim()).filter(Boolean))];
}

function formatArray(value) {
  const arr = uniqueArray(value);
  if (arr.length === 0) return "-";
  return arr.join(", ");
}

function InfoRow({ label, value, multiline = false }) {
  const v = safeStr(value) || "-";

  return (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", minWidth: 190, flexShrink: 0 }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          minWidth: 0,
          ...(multiline
            ? {
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }
            : {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }),
        }}
        title={!multiline ? v : undefined}
      >
        {v}
      </Typography>
    </Stack>
  );
}

function aggregateScanMatches(scanMatches, windowMs = SCAN_GROUP_WINDOW_MS) {
  const prepared = normalizeList(scanMatches)
    .map((item, idx) => {
      const lecsEvent = item?.lecsEvent ?? {};
      const agentEvent = item?.agentEvent ?? {};

      const lecsTimestamp = lecsEvent?.timestamp ?? null;
      const agentTimestamp = agentEvent?.timestamp ?? null;
      const ts = toMillis(lecsTimestamp || agentTimestamp);

      return {
        ...item,
        sourceType: "scan",
        __idx: idx,
        __ts: ts,
        __groupSrcIp: safeStr(lecsEvent?.src_ip).trim(),
        __groupDetail: safeStr(lecsEvent?.signature).trim(),
      };
    })
    .filter((item) => item.__groupSrcIp && item.__groupDetail && item.__ts > 0)
    .sort((a, b) => a.__ts - b.__ts);

  const grouped = [];

  for (const item of prepared) {
    const lastGroup = grouped[grouped.length - 1];

    const canMerge =
      lastGroup &&
      lastGroup.__groupSrcIp === item.__groupSrcIp &&
      lastGroup.__groupDetail === item.__groupDetail &&
      item.__ts - lastGroup.__firstTs <= windowMs;

    if (!canMerge) {
      grouped.push({
        ...item,
        aggregatedCount: 1,
        aggregatedItems: [item],
        aggregatedFrom:
          item?.lecsEvent?.timestamp ?? item?.agentEvent?.timestamp ?? null,
        aggregatedTo:
          item?.lecsEvent?.timestamp ?? item?.agentEvent?.timestamp ?? null,
        __firstTs: item.__ts,
        __lastTs: item.__ts,
      });
      continue;
    }

    lastGroup.aggregatedItems.push(item);
    lastGroup.aggregatedCount += 1;
    lastGroup.aggregatedTo =
      item?.lecsEvent?.timestamp ?? item?.agentEvent?.timestamp ?? lastGroup.aggregatedTo;
    lastGroup.__lastTs = item.__ts;

    const mergedLecsSrcPorts = uniqueArray([
      lastGroup?.lecsEvent?.src_port,
      item?.lecsEvent?.src_port,
    ]);

    const mergedLecsDestIps = uniqueArray([
      lastGroup?.lecsEvent?.dest_ip,
      item?.lecsEvent?.dest_ip,
    ]);

    const mergedLecsDestPorts = uniqueArray([
      lastGroup?.lecsEvent?.dest_port,
      item?.lecsEvent?.dest_port,
    ]);

    const mergedAgentMachineNames = uniqueArray([
      lastGroup?.agentEvent?.machine_name,
      item?.agentEvent?.machine_name,
    ]);

    const mergedAgentProcessNames = uniqueArray([
      lastGroup?.agentEvent?.processName,
      item?.agentEvent?.processName,
    ]);

    const mergedAgentScanTypes = uniqueArray([
      lastGroup?.agentEvent?.scanType,
      item?.agentEvent?.scanType,
    ]);

    const mergedAgentSourceIps = uniqueArray([
      lastGroup?.agentEvent?.source?.ip,
      item?.agentEvent?.source?.ip,
    ]);

    const mergedAgentDestIps = uniqueArray([
      ...(lastGroup?.agentEvent?.metrics?.dest_ips || []),
      ...(item?.agentEvent?.metrics?.dest_ips || []),
    ]);

    const mergedAgentDestPorts = uniqueArray([
      ...(lastGroup?.agentEvent?.metrics?.dest_ports || []),
      ...(item?.agentEvent?.metrics?.dest_ports || []),
    ]);

    lastGroup.lecsEvent = {
      ...lastGroup.lecsEvent,
      src_port: mergedLecsSrcPorts,
      dest_ip: mergedLecsDestIps,
      dest_port: mergedLecsDestPorts,
      signature: lastGroup.__groupDetail,
    };

    lastGroup.agentEvent = {
      ...lastGroup.agentEvent,
      machine_name: mergedAgentMachineNames.join(", "),
      processName: mergedAgentProcessNames.join(", "),
      scanType: mergedAgentScanTypes.join(", "),
      source: {
        ...(lastGroup?.agentEvent?.source || {}),
        ip: mergedAgentSourceIps.join(", "),
      },
      metrics: {
        ...(lastGroup?.agentEvent?.metrics || {}),
        dest_ips: mergedAgentDestIps,
        dest_ports: mergedAgentDestPorts,
      },
    };

    const currentDeltaMs =
      item?.deltaMs === "-" || item?.deltaMs === undefined || item?.deltaMs === null
        ? 0
        : Number(item.deltaMs) || 0;

    const groupDeltaMs =
      lastGroup?.deltaMs === "-" ||
      lastGroup?.deltaMs === undefined ||
      lastGroup?.deltaMs === null
        ? 0
        : Number(lastGroup.deltaMs) || 0;

    lastGroup.deltaMs = Math.max(groupDeltaMs, currentDeltaMs);
    lastGroup.deltaSeconds = Math.round((lastGroup.deltaMs || 0) / 1000);
  }

  return grouped.map((group) => ({
    ...group,
    sourceType: "scan",
  }));
}

const TableMatches = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [filters, setFilters] = useState({
    ioc: true,
    standard: true,
    scan: true,
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    Promise.all([
      API.getMatchAgentLecs({
        licenseCode: sessionStorage.getItem("License"),
      }),
      API.getMatchAgentLecsIoc({
        licenseCode: sessionStorage.getItem("License"),
      }),
      API.getMatchAgentLecsScan({
        licenseCode: sessionStorage.getItem("License"),
      }),
    ])
      .then(([standardResponse, iocResponse, scanResponse]) => {
        if (!mounted) return;

        const standardMatches = normalizeList(standardResponse?.data?.matches).map(
          (item) => ({
            ...item,
            sourceType: "standard",
          })
        );

        const iocMatches = normalizeList(iocResponse?.data?.matches).map((item) => ({
          ...item,
          sourceType: "ioc",
        }));

        const rawScanMatches = normalizeList(scanResponse?.data?.matches).map((item) => ({
          ...item,
          sourceType: "scan",
        }));

        const scanMatches = aggregateScanMatches(rawScanMatches, SCAN_GROUP_WINDOW_MS);

        setMatches([...standardMatches, ...iocMatches, ...scanMatches]);
      })
      .catch((error) => {
        if (!mounted) return;
        console.error("[TableMatches] MATCH ERROR:", error);
        setMatches([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const toggleFilter = useCallback((key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const rows = useMemo(() => {
    return normalizeList(matches)
      .map((item, idx) => {
        const lecsEvent = item?.lecsEvent ?? {};
        const agentEvent = item?.agentEvent ?? {};
        const sourceType = item?.sourceType ?? "standard";

        const lecsTimestamp = lecsEvent?.timestamp ?? null;
        const agentTimestamp = agentEvent?.timestamp ?? null;

        const rowId =
          `${safeStr(sourceType)}-` +
          (
            item?._id ||
            safeStr(lecsEvent?._id) ||
            safeStr(agentEvent?._id) ||
            `${safeStr(lecsTimestamp || "lecs")}-${safeStr(agentTimestamp || "agent")}-${idx}`
          );

        return {
          id: rowId,

          sourceType,
          matchType: safeStr(item?.matchType) || "-",
          deltaMs: item?.deltaMs ?? "-",
          deltaSeconds: item?.deltaSeconds ?? "-",
          aggregatedCount: item?.aggregatedCount ?? 1,
          aggregatedFrom: item?.aggregatedFrom ?? null,
          aggregatedTo: item?.aggregatedTo ?? null,

          lecsTimestamp,
          lecsSeverity: mapSeverity(lecsEvent?.severity),
          lecsSrc: formatIpPort(
            lecsEvent?.src_ip,
            Array.isArray(lecsEvent?.src_port)
              ? uniqueArray(lecsEvent?.src_port).join(", ")
              : lecsEvent?.src_port
          ),
          lecsDest: formatIpPort(
            Array.isArray(lecsEvent?.dest_ip)
              ? uniqueArray(lecsEvent?.dest_ip).join(", ")
              : lecsEvent?.dest_ip,
            Array.isArray(lecsEvent?.dest_port)
              ? uniqueArray(lecsEvent?.dest_port).join(", ")
              : lecsEvent?.dest_port
          ),
          lecsSignature: safeStr(lecsEvent?.signature) || "-",

          agentTimestamp,
          agentMachineName: safeStr(agentEvent?.machine_name) || "-",

          // STANDARD / IOC
          agentProcessName:
            safeStr(agentEvent?.eventData?.source?.processName) || "-",
          agentPid: safeStr(agentEvent?.eventData?.source?.pid) || "-",
          agentCountry:
            safeStr(agentEvent?.eventData?.metrics?.geo?.country) || "-",
          agentSourceIp:
            safeStr(agentEvent?.eventData?.source?.localIp) || "-",
          agentTargetIp:
            safeStr(agentEvent?.eventData?.target?.remoteIp) || "-",
          agentPort:
            safeStr(agentEvent?.eventData?.source?.remotePort) || "-",
          subtype: safeStr(agentEvent?.eventData?.subtype) || "-",

          // SCAN
          agentScanProcessName: safeStr(agentEvent?.processName) || "-",
          agentScanType: safeStr(agentEvent?.scanType) || "-",
          agentScanSourceIp: safeStr(agentEvent?.source?.ip) || "-",
          agentScanDestIps: formatArray(agentEvent?.metrics?.dest_ips),
          agentScanDestPorts: formatArray(agentEvent?.metrics?.dest_ports),

          sortTimestamp: Math.max(
            toMillis(lecsTimestamp),
            toMillis(agentTimestamp)
          ),

          raw: item,
        };
      })
      .filter((row) => filters[row.sourceType])
      .sort((a, b) => b.sortTimestamp - a.sortTimestamp);
  }, [matches, filters]);

  const columns = useMemo(
    () => [
      {
        field: "sourceType",
        headerName: "Type",
        flex: 0.9,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
          const value = params.value;

          if (value === "ioc") {
            return (
              <Chip
                size="small"
                variant="outlined"
                label="IOC"
                color="warning"
              />
            );
          }

          if (value === "scan") {
            return (
              <Chip
                size="small"
                variant="outlined"
                label="SCAN"
                color="success"
              />
            );
          }

          return (
            <Chip
              size="small"
              variant="outlined"
              label="NETWORK ANOMALY"
              color="primary"
            />
          );
        },
      },
      {
        field: "lecsTimestamp",
        headerName: "Timestamp",
        flex: 1.2,
        minWidth: 190,
        renderCell: (params) => formatDate(params.value),
        sortComparator: (v1, v2, param1, param2) =>
          (param1?.row?.sortTimestamp ?? 0) - (param2?.row?.sortTimestamp ?? 0),
      },
      {
        field: "lecsSeverity",
        headerName: "Severity",
        flex: 0.9,
        minWidth: 130,
      },
      {
        field: "lecsSrc",
        headerName: "Source",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "lecsDest",
        headerName: "Destination",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "lecsSignature",
        headerName: "Detail",
        flex: 1.5,
        minWidth: 240,
      },
      {
        field: "aggregatedCount",
        headerName: "Grouped",
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          if (params.row.sourceType !== "scan") return "-";

          const count = Number(params.value) || 1;

          return (
            <Chip
              size="small"
              label={count}
              color={count > 1 ? "success" : "default"}
              variant={count > 1 ? "filled" : "outlined"}
            />
          );
        },
      },
    ],
    []
  );

  const handleRowClick = useCallback((params) => {
    setSelectedRow(params.row);
    setDetailOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDetailOpen(false);
    setSelectedRow(null);
  }, []);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2, background: '#1e1e1e' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: "wrap", gap: 1, mb: 1.5 }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>LECS / AGENT Matches</Typography>

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          <Chip
            label="IOC"
            size="small"
            color="warning"
            variant={filters.ioc ? "filled" : "outlined"}
            onClick={() => toggleFilter("ioc")}
            sx={{ cursor: "pointer" }}
          />
          <Chip
            label="NETWORK ANOMALY"
            size="small"
            color="primary"
            variant={filters.standard ? "filled" : "outlined"}
            onClick={() => toggleFilter("standard")}
            sx={{ cursor: "pointer" }}
          />
          <Chip
            label="SCAN"
            size="small"
            color="success"
            variant={filters.scan ? "filled" : "outlined"}
            onClick={() => toggleFilter("scan")}
            sx={{ cursor: "pointer" }}
          />
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGridPro
          loading={loading}
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          localeText={{ noRowsLabel: "No matches detected" }}
          hideFooter
          initialState={{
            sorting: {
              sortModel: [{ field: "lecsTimestamp", sort: "desc" }],
            },
          }}
          sx={{ height: "100%", "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      <Dialog
        open={detailOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Agent Event Details
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedRow ? (
            <Stack spacing={1.25}>
              <InfoRow
                label="Record Type"
                value={
                  selectedRow.sourceType === "ioc"
                    ? "IOC Match"
                    : selectedRow.sourceType === "scan"
                    ? "Scan Match"
                    : "Network Anomaly Match"
                }
              />

              <InfoRow label="Delta Milliseconds" value={selectedRow.deltaMs} />

              {selectedRow.sourceType === "standard" && (
                <InfoRow label="Subtype" value={selectedRow.subtype} />
              )}

              {selectedRow.sourceType === "scan" ? (
                <>
                  <InfoRow label="Grouped Events" value={selectedRow.aggregatedCount} />
                  <InfoRow
                    label="Window Start"
                    value={formatDate(selectedRow.aggregatedFrom)}
                  />
                  <InfoRow
                    label="Window End"
                    value={formatDate(selectedRow.aggregatedTo)}
                  />
                  <InfoRow label="Machine Name" value={selectedRow.agentMachineName} multiline />
                  <InfoRow
                    label="Process Name"
                    value={selectedRow.agentScanProcessName}
                    multiline
                  />
                  <InfoRow
                    label="Scan Type"
                    value={selectedRow.agentScanType}
                    multiline
                  />
                  <InfoRow
                    label="Source IP"
                    value={selectedRow.agentScanSourceIp}
                    multiline
                  />
                  <InfoRow
                    label="Destination IPs"
                    value={selectedRow.agentScanDestIps}
                    multiline
                  />
                  <InfoRow
                    label="Destination Ports"
                    value={selectedRow.agentScanDestPorts}
                    multiline
                  />
                </>
              ) : (
                <>
                  <InfoRow
                    label="Agent Timestamp"
                    value={formatDate(selectedRow.agentTimestamp)}
                  />
                  <InfoRow label="Machine Name" value={selectedRow.agentMachineName} />
                  <InfoRow label="Process Name" value={selectedRow.agentProcessName} />

                  {selectedRow.sourceType === "ioc" && (
                    <InfoRow label="Country" value={selectedRow.agentCountry} />
                  )}

                  {selectedRow.sourceType === "standard" && (
                    <InfoRow label="PID" value={selectedRow.agentPid} />
                  )}
                </>
              )}
            </Stack>
          ) : (
            <Typography variant="body2">No details available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TableMatches;